import React, { useState, useRef, useEffect } from "react";
import { images } from "../../assets/images";
import ingredientsList from "../../data/lunchIngredients";
import IngredientTag from "../IngredientTag";
import QuestionsSection from "../QuestionsSection";
import RecipeResults from "../RecipeResults";
import FridgeScanner from "../FridgeScanner";
import "./LunchRecipeBuilder.css";
import { Info, InfoIcon } from "lucide-react";
import LunchRecipeResults from "../WidgetLunchRecipeResult/LunchRecipeResult";
import LunchQuestionsSection from "../LunchQuestionSection/LunchQuestionSection";

// Check icon for suggestions
const checkIcon =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23007A33' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20 6 9 17 4 12'%3E%3C/polyline%3E%3C/svg%3E";

// Camera icon for scan button
const cameraIcon =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%23007A33' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z'%3E%3C/path%3E%3Ccircle cx='12' cy='13' r='4'%3E%3C/circle%3E%3C/svg%3E";

// Steps in the recipe builder flow
const STEPS = {
  INGREDIENTS: "ingredients",
  QUESTIONS: "questions",
  RECIPES: "recipes",
};

function LunchRecipeBuilder({ onSelectRecipe, setShowBanner, scrollToTop }) {
  const [currentStep, setCurrentStep] = useState(STEPS.INGREDIENTS);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0);
  const [recipePreferences, setRecipePreferences] = useState(null);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [hasScanned, setHasScanned] = useState(false);
  const [showManualInput, setShowManualInput] = useState(false);
  const inputRef = useRef(null);

  const suggestionsRef = useRef(null);
  const sectionRef = useRef(null);

  const isButtonEnabled = selectedIngredients.length >= 3;

  // Scroll to top on step change - targeting the correct scroll container
  useEffect(() => {
    // Scroll to the TopBar via the parent callback
    if (scrollToTop) {
      scrollToTop();
    }
    if (currentStep !== STEPS.INGREDIENTS) {
      setShowBanner(false);
    } else {
      setShowBanner(true);
    }
  }, [currentStep]);

  useEffect(() => {
    const trimmedSearch = searchTerm.trim();

    // remove leading spaces
    if (trimmedSearch.length === 0) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const lowerSearch = trimmedSearch.toLowerCase();
    const tokens = lowerSearch.split(/\s+/).filter(Boolean);
    const isShort = lowerSearch.length < 3;

    const rankMatches = (list) => {
      const exact = [];
      const starts = [];
      const wordStarts = [];
      const contains = [];

      list.forEach((item) => {
        if (selectedIngredients.includes(item)) return;

        const itemLower = item.toLowerCase();
        const words = itemLower.split(/\s+/);

        if (tokens.length > 1) {
          const allTokensPresent = tokens.every((t) => itemLower.includes(t));
          if (!allTokensPresent) return;
        }

        if (itemLower === lowerSearch) {
          exact.push(item);
          return;
        }
        if (itemLower.startsWith(lowerSearch)) {
          starts.push(item);
          return;
        }
        if (words.some((w) => w.startsWith(lowerSearch))) {
          wordStarts.push(item);
          return;
        }
        if (!isShort && itemLower.includes(lowerSearch)) {
          contains.push(item);
        }
      });

      return [...exact, ...starts, ...wordStarts, ...contains];
    };

    const priorityList = ingredientsList.slice(0, 14);
    const alphabeticalList = ingredientsList.slice(14);

    const results = [
      ...rankMatches(priorityList),
      ...rankMatches(alphabeticalList),
    ];
    setSuggestions(results);
    setShowSuggestions(results.length > 0);
    setActiveSuggestionIndex(0);
  }, [searchTerm, selectedIngredients]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSelectIngredient = (ingredient) => {
    if (!selectedIngredients.includes(ingredient)) {
      setSelectedIngredients([...selectedIngredients, ingredient]);
    }
    setSearchTerm("");
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const handleRemoveIngredient = (ingredient) => {
    setSelectedIngredients(
      selectedIngredients.filter((item) => item !== ingredient),
    );
  };

  const handleClearAll = () => {
    setSelectedIngredients([]);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveSuggestionIndex((prev) =>
        prev < suggestions.length - 1 ? prev + 1 : prev,
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveSuggestionIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === "Enter" && showSuggestions && suggestions.length > 0) {
      e.preventDefault();
      handleSelectIngredient(suggestions[activeSuggestionIndex]);
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const handleContinue = () => {
    if (isButtonEnabled) {
      setCurrentStep(STEPS.QUESTIONS);
    }
  };

  const handleShowRecipes = (preferences) => {
    setRecipePreferences(preferences);
    console.log("Recipe Preferences:", {
      ingredients: selectedIngredients,
      ...preferences,
    });
    setCurrentStep(STEPS.RECIPES);
  };

  const handleBackToIngredients = () => {
    setCurrentStep(STEPS.INGREDIENTS);
  };

  const handleBackToQuestions = () => {
    setCurrentStep(STEPS.QUESTIONS);
    setRecipePreferences(null);
  };

  const handleBackToHome = () => {
    setCurrentStep(STEPS.INGREDIENTS);
  };

  // Handle ingredients from fridge scanner
  const handleIngredientsFromScanner = (scannedIngredients) => {
    // Add scanned ingredients, avoiding duplicates
    const newIngredients = scannedIngredients.filter(
      (ingredient) => !selectedIngredients.includes(ingredient),
    );
    setShowManualInput(true);
    setSelectedIngredients((prev) => [...prev, ...newIngredients]);
    setHasScanned(true);
  };

  // Render Questions Step
  if (currentStep === STEPS.QUESTIONS) {
    return (
      <section className="recipe-builder" ref={sectionRef}>
        <div className="recipe-builder-container questions-step">
          <button
            className="back-button"
            onClick={handleBackToIngredients}
            type="button"
          >
            ← Back to ingredients
          </button>
          <LunchQuestionsSection onShowRecipes={handleShowRecipes} />
        </div>
      </section>
    );
  }

  // Render Recipes Step
  if (currentStep === STEPS.RECIPES) {
    return (
      <div ref={sectionRef}>
        <LunchRecipeResults
          ingredients={selectedIngredients}
          preferences={recipePreferences}
          onBackToQuestions={handleBackToQuestions}
          onBackToHome={handleBackToHome}
          onSelectRecipe={onSelectRecipe}
        />
      </div>
    );
  }

  // Render Ingredients Step (default)
  return (
    <section className="recipe-builder" ref={sectionRef}>
      <div className="recipe-builder-container">
        {/* Left Section - Ingredient Input */}
        <div className="recipe-builder-left">
          {/* <h1>Test Environment</h1> */}
          <div className={`recipe-header-till-button`}>
            {!showManualInput && (
              <button
                className="scan-fridge-btn manual-input-btn"
                onClick={() => setShowManualInput(true)}
              >
                Enter Ingredients Manually
              </button>
            )}
            <div
              className={`afterManual ${!showManualInput ? "manual-not-active" : ""}`}
            >
              <div className="recipe-builder-input-section">
                <div className="input-group">
                  <label
                    className="input-label add-ingredients"
                    htmlFor="ingredients"
                  >
                    Add your ingredients
                  </label>
                  <div className="input-wrapper">
                    <input
                      ref={inputRef}
                      type="text"
                      id="ingredients"
                      className="ingredient-input"
                      placeholder="Enter your ingredients..."
                      value={searchTerm}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyDown}
                      onFocus={() => {
                        if (suggestions.length > 0) setShowSuggestions(true);
                      }}
                      autoComplete="off"
                    />

                    {/* Suggestions Dropdown */}
                    {showSuggestions && (
                      <div
                        className="suggestions-dropdown"
                        ref={suggestionsRef}
                      >
                        {suggestions.map((suggestion, index) => (
                          <div
                            key={suggestion}
                            className={`suggestion-item ${index === activeSuggestionIndex ? "active" : ""}`}
                            onClick={() => handleSelectIngredient(suggestion)}
                          >
                            <span className="suggestion-text">
                              {suggestion}
                            </span>
                            <img
                              src={checkIcon}
                              alt=""
                              className="suggestion-check"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <p className="minimum-text">
                  Minimum 3 ingredients<span className="asterisk">*</span>
                </p>

                {/* Selected Ingredients */}
                {selectedIngredients.length > 0 && (
                  <div className="selected-ingredients-section">
                    <div className="selected-ingredients-header">
                      <button
                        className="clear-all-button"
                        onClick={handleClearAll}
                      >
                        Clear all
                      </button>
                    </div>
                    <div className="selected-ingredients-list">
                      {selectedIngredients.map((ingredient) => (
                        <IngredientTag
                          key={ingredient}
                          ingredient={ingredient}
                          onRemove={handleRemoveIngredient}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <button
                className={`continue-button ${isButtonEnabled ? "enabled" : "disabled"}`}
                disabled={!isButtonEnabled}
                onClick={handleContinue}
                type="button"
              >
                Continue
              </button>
              <div className="t-c-icon-text-desktop">
                <Info className="info-icon" strokeWidth={1.25}/>
                <div className="dextop-terms-conditions">
                  <p>
                    By proceeding, you agree to the
                    <a
                      className="terms-condition-knorr"
                      href="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms & Conditions
                    </a>
                    associated with the usage of both this website and Knorr
                    Recipe AI.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile/Tablet Scan Fridge Button */}
          <div className="mobile-scan-section">
            {/* <div className="mobile-or-divider">
              <span className="or-text">OR</span>
            </div> */}
            <button
              className="scan-fridge-btn"
              onClick={() => setIsScannerOpen(true)}
            >
              <img src={cameraIcon} alt="" />
              {hasScanned ? "Retake photo" : "Start Scanning"}
            </button>
          </div>
        </div>

        {/* OR Divider - Desktop only */}
        <div className="or-divider or-divider-desktop">
          <span className="or-text">OR</span>
        </div>

        {/* Right Section - QR Code (Desktop only) */}
        <div className="recipe-builder-right recipe-builder-right-desktop">
          <div className="qr-card qr-card-desktop">
            <p className="qr-description">
              Scan your fridge from your phone to instantly add ingredients.
            </p>
            <div className="qr-code-wrapper">
              <img
                src={images.qrCode}
                alt="QR Code to scan fridge"
                className="qr-code"
              />
            </div>
          </div>
        </div>

        {/* Fridge Scanner Modal */}
        <FridgeScanner
          isOpen={isScannerOpen}
          onClose={() => setIsScannerOpen(false)}
          onIngredientsDetected={handleIngredientsFromScanner}
        />
      </div>
      <div className="t-c-icon-text-mobile">
        <Info className="info-icon" strokeWidth={1.25} />
        <div className="term-condition-mobile">
          <p>
            By proceeding, you agree to the
            <a
              className="terms-condition-knorr"
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms & Conditions
            </a>
            associated with the usage of both this website and Knorr Recipe AI.
          </p>
        </div>
      </div>
    </section>
  );
}

export default LunchRecipeBuilder;
